import { Alert } from '@mui/material'
import { useFormikContext } from 'formik'
import moment from 'moment-timezone'
import { useMemo } from 'react'

import ParamsGrid from 'components/attacks/create/ParamsGrid'
import TriggerField from 'components/attacks/create/trigger/TriggerField'
import { useRestrictedTimeWindow } from 'hooks/use-restricted-time-window'
import { useTranslation } from 'hooks/use-translation'
import { WEEK_DAYS } from 'utils/days'

function RandomTrigger() {
  const { trans } = useTranslation()

  const { values } = useFormikContext()

  const { checkAttackTrigger } = useRestrictedTimeWindow()
  const restrictedTimeWindow = useMemo(() => {
    if (values.activeDays && values.end && values.start) {
      const restrictedTimeWindowForDay = checkAttackTrigger({
        activeDays: values.activeDays,
        end: values.end,
        start: values.start,
        timeZone: values.timeZone
      })

      if (!restrictedTimeWindowForDay) return {}

      const dayIndex = WEEK_DAYS.indexOf(restrictedTimeWindowForDay.day)
      const windowStart = moment(
        restrictedTimeWindowForDay.window.start,
        'HH:mm'
      )
        .tz(values.timeZone ?? 'UTC')
        .day(dayIndex)
      const windowEnd = moment(restrictedTimeWindowForDay.window.end, 'HH:mm')
        .tz(values.timeZone ?? 'UTC')
        .day(dayIndex)

      if (windowEnd.isBefore(windowStart)) {
        windowEnd.add(1, 'day')
      }

      return {
        ...restrictedTimeWindowForDay,
        window: { start: windowStart, end: windowEnd }
      }
    }

    return {}
  }, [
    values.activeDays,
    values.start,
    values.end,
    values.timeZone,
    checkAttackTrigger
  ])

  return (
    <ParamsGrid>
      <TriggerField
        data-test-random-executions-input
        rightAligned
        description="The number of times run per run-window"
        fieldName="maxRuns"
        min={1}
        placeholder="#"
        title="Runs per day"
        type="number"
      />
      <TriggerField
        data-test-random-from-input
        masked
        required
        description="Local time, 24-hour format"
        fieldName="start"
        mask="11:11"
        placeholder={moment().add(1, 'hour').startOf('hour').format('HH:mm')}
        title="Start of run-window"
      />
      <TriggerField
        data-test-random-to-input
        masked
        required
        description="Local time, 24-hour format"
        fieldName="end"
        mask="11:11"
        placeholder={moment().add(2, 'hour').startOf('hour').format('HH:mm')}
        title="End of run-window"
      />

      {restrictedTimeWindow?.window?.start &&
        restrictedTimeWindow?.window?.end && (
          <Alert
            css={{ gridColumnStart: 1, gridColumnEnd: 5 }}
            severity="warning"
          >
            {trans('restricted_time_window.schedule_alert_with_day', {
              start: restrictedTimeWindow.window.start.format('h:mm A, dddd'),
              end: restrictedTimeWindow.window.end.format('h:mm A, dddd')
            })}
          </Alert>
        )}
    </ParamsGrid>
  )
}

export default RandomTrigger
